(() => {
  const media = window.matchMedia('(max-width: 1023px)');
  const dictionaries = {
    de: {
      overview: 'Übersicht', areas: 'Shop-Bereiche', area: 'Bereich', category: 'Kategorie',
      subcategories: 'Unterkategorien', choose: 'Kategorie auswählen',
      hint: 'Wähle links eine Kategorie, um die passenden Unterkategorien zu sehen.',
      all: title => `Alle in ${title} anzeigen`, loading: 'Kategorien werden geladen …',
      error: 'Die Kategorien konnten gerade nicht geladen werden. ', fallback: 'Zum Shop-Bereich'
    },
    fr: {
      overview: 'Vue d’ensemble', areas: 'Univers de la boutique', area: 'Univers', category: 'Catégorie',
      subcategories: 'Sous-catégories', choose: 'Choisissez une catégorie',
      hint: 'Choisissez une catégorie à gauche pour voir les sous-catégories.',
      all: title => `Voir tout dans ${title}`, loading: 'Chargement des catégories…',
      error: 'Impossible de charger les catégories. ', fallback: 'Voir cet univers'
    },
    it: {
      overview: 'Panoramica', areas: 'Reparti del negozio', area: 'Reparto', category: 'Categoria',
      subcategories: 'Sottocategorie', choose: 'Scegli una categoria',
      hint: 'Scegli una categoria a sinistra per vedere le sottocategorie.',
      all: title => `Vedi tutto in ${title}`, loading: 'Caricamento delle categorie…',
      error: 'Impossibile caricare le categorie. ', fallback: 'Vai al reparto'
    }
  };
  const labels = dictionaries[document.documentElement.lang.toLowerCase().slice(0, 2)] || dictionaries.de;
  let treePromise;
  let tree;
  let mode = null;
  let requestedUrl = null;
  let selectedRoot = null;
  let selectedChild = null;
  let opener = null;

  const desktop = () => document.querySelector('[data-camping-category-desktop]');
  const mobile = () => document.querySelector('[data-camping-category-mobile]');
  const mobileNavigation = () => mobile()?.closest('mobile-navigation');

  const normalizedPath = value => {
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch (_) {
      return '';
    }
  };

  const linkUrl = value => {
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
    } catch (_) {
      return '#';
    }
  };

  const setExpanded = () => {
    document.querySelectorAll('[data-camping-category-root]').forEach(trigger => {
      const expanded = mode !== null && normalizedPath(trigger.dataset.campingCategoryRoot) === normalizedPath(requestedUrl);
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  };

  const close = (restoreFocus = false) => {
    desktop()?.setAttribute('hidden', '');
    mobile()?.setAttribute('hidden', '');
    mobileNavigation()?.classList.remove('camping-category-active');
    if (mode === 'mobile') mobileNavigation()?.reset?.();
    const focusTarget = restoreFocus ? opener : null;
    mode = null;
    requestedUrl = null;
    selectedRoot = null;
    selectedChild = null;
    opener = null;
    setExpanded();
    focusTarget?.focus({ preventScroll: true });
  };

  const loadTree = () => {
    if (tree) return Promise.resolve(tree);
    if (treePromise) return treePromise;
    const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
    url.searchParams.set('section_id', 'camping-category-data');
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    treePromise = fetch(url, { credentials: 'same-origin' })
      .then(response => {
        if (!response.ok) throw new Error('Category section unavailable');
        return response.text();
      })
      .then(html => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const template = documentFragment.querySelector('template[data-camping-category-data]');
        if (!template) throw new Error('Category data missing');
        const parsed = JSON.parse(template.content.textContent);
        if (!Array.isArray(parsed) || !parsed.length) throw new Error('Category menu empty');
        tree = parsed;
        return tree;
      })
      .catch(error => {
        treePromise = null;
        throw error;
      });
    return treePromise;
  };

  const createLink = (label, url, className) => {
    const link = document.createElement('a');
    link.className = className;
    link.href = linkUrl(url);
    link.textContent = label;
    return link;
  };

  const createRow = (node, action, active = false) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const row = hasChildren && action ? document.createElement('button') : document.createElement('a');
    row.className = `camping-category-row${active ? ' is-active' : ''}`;
    if (row.tagName === 'BUTTON') {
      row.type = 'button';
      row.dataset[action.name] = action.index;
      row.setAttribute('aria-pressed', active ? 'true' : 'false');
    } else {
      row.href = linkUrl(node.url);
    }
    const title = document.createElement('span');
    title.textContent = node.title;
    row.append(title);
    if (hasChildren) {
      const arrow = document.createElement('span');
      arrow.className = 'camping-category-row__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      row.append(arrow);
    }
    return row;
  };

  const addHeading = (container, eyebrow, title, url) => {
    const heading = document.createElement('div');
    heading.className = 'camping-category-column-heading';
    const small = document.createElement('span');
    small.className = 'camping-category-column-heading__eyebrow';
    small.textContent = eyebrow;
    const strong = document.createElement('strong');
    strong.textContent = title;
    heading.append(small, strong);
    container.append(heading);
    if (url) container.append(createLink(labels.all(title), url, 'camping-category-all'));
  };

  const renderDesktop = () => {
    const panel = desktop();
    if (!panel || !selectedRoot || !tree) return;
    const roots = panel.querySelector('[data-camping-category-roots]');
    const children = panel.querySelector('[data-camping-category-children]');
    const grandchildren = panel.querySelector('[data-camping-category-grandchildren]');
    roots.replaceChildren();
    children.replaceChildren();
    grandchildren.replaceChildren();

    addHeading(roots, labels.overview, labels.areas);
    tree.forEach((root, index) => {
      if (normalizedPath(root.url).includes('/collections/') && Array.isArray(root.children) && root.children.length) {
        roots.append(createRow(root, { name: 'campingSelectRoot', index }, root === selectedRoot));
      }
    });

    addHeading(children, labels.area, selectedRoot.title, selectedRoot.url);
    selectedRoot.children.forEach((child, index) => {
      children.append(createRow(child, { name: 'campingSelectChild', index }, child === selectedChild));
    });

    if (selectedChild) {
      addHeading(grandchildren, labels.category, selectedChild.title, selectedChild.url);
      selectedChild.children.forEach(node => grandchildren.append(createRow(node)));
    } else {
      addHeading(grandchildren, labels.subcategories, labels.choose);
      const hint = document.createElement('p');
      hint.className = 'camping-category-hint';
      hint.textContent = labels.hint;
      grandchildren.append(hint);
    }
    panel.querySelector('[data-camping-category-status]').hidden = true;
    panel.querySelector('[data-camping-category-columns]').hidden = false;
  };

  const renderMobile = () => {
    const panel = mobile();
    if (!panel || !selectedRoot) return;
    const current = selectedChild || selectedRoot;
    panel.querySelector('[data-camping-category-mobile-title]').textContent = current.title;
    const all = panel.querySelector('[data-camping-category-mobile-all]');
    all.href = linkUrl(current.url);
    all.textContent = labels.all(current.title);
    all.hidden = false;
    const items = panel.querySelector('[data-camping-category-mobile-items]');
    items.replaceChildren();
    current.children.forEach((node, index) => {
      const action = selectedChild ? null : { name: 'campingMobileChild', index };
      items.append(createRow(node, action));
    });
    panel.querySelector('[data-camping-category-mobile-status]').hidden = true;
    mobileNavigation()?.closest('.sidebar__body')?.scrollTo({ top: 0 });
  };

  const showError = () => {
    const status = mode === 'mobile'
      ? mobile()?.querySelector('[data-camping-category-mobile-status]')
      : desktop()?.querySelector('[data-camping-category-status]');
    if (!status) return;
    status.replaceChildren(document.createTextNode(labels.error));
    status.append(createLink(labels.fallback, requestedUrl, 'camping-category-error-link'));
    status.hidden = false;
  };

  const open = (url, trigger) => {
    const nextMode = media.matches ? 'mobile' : 'desktop';
    if (mode === 'desktop' && nextMode === 'desktop' && normalizedPath(url) === normalizedPath(requestedUrl)) {
      close(true);
      return;
    }
    close();
    document.querySelectorAll('search-form.camping-search-open').forEach(form => {
      form.classList.remove('camping-search-open');
      form.querySelector('[data-js-search-input]')?.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll('.camping-search-backdrop.active').forEach(backdrop => backdrop.classList.remove('active'));
    mode = nextMode;
    requestedUrl = url;
    opener = trigger;
    setExpanded();

    if (mode === 'desktop') {
      const panel = desktop();
      if (!panel) return close();
      panel.hidden = false;
      panel.querySelector('[data-camping-category-columns]').hidden = true;
      const status = panel.querySelector('[data-camping-category-status]');
      status.textContent = labels.loading;
      status.hidden = false;
      panel.focus({ preventScroll: true });
    } else {
      const panel = mobile();
      if (!panel) return close();
      mobileNavigation()?.reset?.();
      mobileNavigation()?.classList.add('camping-category-active');
      panel.hidden = false;
      panel.querySelector('[data-camping-category-mobile-all]').hidden = true;
      panel.querySelector('[data-camping-category-mobile-items]').replaceChildren();
      panel.querySelector('[data-camping-category-mobile-title]').textContent = labels.areas;
      const status = panel.querySelector('[data-camping-category-mobile-status]');
      status.textContent = labels.loading;
      status.hidden = false;
      panel.querySelector('[data-camping-category-back]')?.focus({ preventScroll: true });
    }

    loadTree().then(data => {
      if (!mode || normalizedPath(requestedUrl) !== normalizedPath(url)) return;
      selectedRoot = data.find(root => normalizedPath(root.url) === normalizedPath(url));
      if (!selectedRoot || !Array.isArray(selectedRoot.children)) throw new Error('Category branch missing');
      selectedChild = mode === 'desktop'
        ? selectedRoot.children.find(child => Array.isArray(child.children) && child.children.length) || null
        : null;
      if (mode === 'desktop') {
        renderDesktop();
        desktop()?.focus({ preventScroll: true });
      } else {
        renderMobile();
        mobile()?.querySelector('[data-camping-category-back]')?.focus({ preventScroll: true });
      }
    }).catch(() => {
      if (mode && normalizedPath(requestedUrl) === normalizedPath(url)) showError();
    });
  };

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-camping-category-root]');
    if (trigger) {
      event.preventDefault();
      open(trigger.dataset.campingCategoryRoot, trigger);
      return;
    }
    if (event.target.closest('[data-camping-category-close]')) {
      close(true);
      return;
    }
    if (event.target.closest('[data-camping-category-back]')) {
      if (selectedChild) {
        selectedChild = null;
        renderMobile();
        mobile()?.querySelector('[data-camping-category-back]')?.focus({ preventScroll: true });
      } else {
        close(true);
      }
      return;
    }
    const rootButton = event.target.closest('[data-camping-select-root]');
    if (rootButton && mode === 'desktop') {
      selectedRoot = tree[Number(rootButton.dataset.campingSelectRoot)];
      selectedChild = selectedRoot.children.find(child => Array.isArray(child.children) && child.children.length) || null;
      requestedUrl = selectedRoot.url;
      setExpanded();
      renderDesktop();
      desktop()?.querySelector(`[data-camping-select-root="${rootButton.dataset.campingSelectRoot}"]`)?.focus({ preventScroll: true });
      return;
    }
    const childButton = event.target.closest('[data-camping-select-child]');
    if (childButton && mode === 'desktop') {
      selectedChild = selectedRoot.children[Number(childButton.dataset.campingSelectChild)];
      renderDesktop();
      desktop()?.querySelector(`[data-camping-select-child="${childButton.dataset.campingSelectChild}"]`)?.focus({ preventScroll: true });
      return;
    }
    const mobileChild = event.target.closest('[data-camping-mobile-child]');
    if (mobileChild && mode === 'mobile') {
      selectedChild = selectedRoot.children[Number(mobileChild.dataset.campingMobileChild)];
      renderMobile();
      mobile()?.querySelector('[data-camping-category-back]')?.focus({ preventScroll: true });
    }
  });

  document.addEventListener('pointerdown', event => {
    if (mode !== 'desktop') return;
    if (event.target.closest('[data-camping-category-desktop], [data-camping-category-root]')) return;
    close();
  });

  document.addEventListener('focusin', event => {
    if (mode !== 'desktop') return;
    if (event.target.closest('[data-camping-category-desktop], [data-camping-category-root]')) return;
    close();
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !mode) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    close(true);
  }, true);

  media.addEventListener('change', () => close());
  document.addEventListener('shopify:section:unload', () => close());
  window.campingCategoryMenu = { closeMobile: () => { if (mode === 'mobile') close(); } };
})();
